// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { JwtHelperService } from '@auth0/angular-jwt';
import { classToPlain, plainToClass } from 'class-transformer';
import { CookieService } from 'ngx-cookie-service';

// Rxjs import,
import { Observable, Subject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// CC-Common imports
import { Utils } from 'cc-common';

// Service Import
import { MPCacheRegistrationService } from './shared-services/mp-cache-registration-service';

// Model imports
import { LoginParameterDto } from '../_models/login-parameter-dto';
import { UserInfoResultDto } from '../_models/user-info-result-dto';
import { BrandingConfigurationResultDto } from '../_models/branding-configuration-result-dto';

// Constant imports
import { URLConstants } from '../_constants/url-constants';
import { StorageKeyConstants } from '../_constants/storage-key-constants';
import { CacheDuration } from '../_constants/cache-duration';
import { Constants } from '../_constants/constants';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    jwtHelper = new JwtHelperService();

    decodedToken: any;
    private readonly _subjectLoginSuccess: Subject<boolean> = new Subject(); // to notify other component that users have logged in successfully.
    private readonly _subjectProfileImageUpdated: Subject<string> = new Subject(); // notify when profile image is updated successfully
    private readonly _subjectLogout: Subject<boolean> = new Subject(); // notify when logout is clicked

    constructor(
        private readonly http: HttpClient,
        private readonly cookieService: CookieService,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    get isLoggedIn(): boolean {
        let loggedIn = false;
        const token = localStorage.getItem(StorageKeyConstants.apiTokenName);
        if (!!token) {
            loggedIn = !this.jwtHelper.isTokenExpired(token);
        }
        return loggedIn;
    }

    get isTokenExpired(): boolean {
        let expired = false;
        const token = localStorage.getItem(StorageKeyConstants.apiTokenName);
        if (!!token) {
            expired = this.jwtHelper.isTokenExpired(token);
        }
        return expired;
    }

    get hasFormsAuthenticationCookie() {
        return this.cookieService.check(StorageKeyConstants.clientFormsAuthenticationCookieName);
    }

    getSubjectLoginSuccess(): Subject<boolean> {
        return this._subjectLoginSuccess;
    }

    updateSubjectLoginSuccess(value: boolean): void {
        this._subjectLoginSuccess.next(value);
    }

    logOut(queryParamsValue: string = null) {
        this.clearAuthLocalStorageData();
        this.cacheRegistrationService.deleteAllCache();
        this.updateSubjectLoginSuccess(false);
        this.updateSubjectLogout(true);
        this.decodedToken = null;
        if (queryParamsValue === null) {
            location.href = 'user/login';
        } else {
            location.href = `user/login?returnUrl=${encodeURIComponent(queryParamsValue)}`;
        }
    }

    getSubjectLogout(): Subject<boolean> {
        return this._subjectLogout;
    }

    updateSubjectLogout(value: boolean): void {
        this._subjectLogout.next(value);
    }

    getSubjectProfileImageUpdated(): Subject<string> {
        return this._subjectProfileImageUpdated;
    }

    updateSubjectProfileImageUpdated(value: string): void {
        this._subjectProfileImageUpdated.next(value);
    }

    getTokenValidityTime(): number {
        const token = localStorage.getItem(StorageKeyConstants.apiTokenName);
        if (!!token) {
            const expiry = this.jwtHelper.getTokenExpirationDate(token);
            const validity = expiry.getTime() - Date.now();
            const maxValidity = 24 * 60 * 60 * 1000; // 24hrs
            // To fix 23 hr / 60 min displayed post login, removing a minute
            return Math.min(validity, maxValidity - 60000);
        }
        return -1;
    }

    extendSession(userPassword: string): Observable<boolean> {
        const params = new LoginParameterDto();
        params.username = this.decodedToken.nameid;
        params.password = userPassword;
        params.portalType = Constants.portalType;
        return this.http
            .post(URLConstants.ccAuthApiBaseUrl + 'login', classToPlain(params))
            .pipe(catchError(() => of(null)))
            .pipe(
                map((response: any) => {
                    const user = plainToClass(UserInfoResultDto, response);
                    if (Utils.notNullAndDefined(user)) {
                        this.setUser(user);
                        this.setToken(user.token);
                        this.setDecodedToken();
                    }
                    return Utils.notNullAndDefined(user);
                })
            );
    }

    login(loginParameterDto: LoginParameterDto): Observable<UserInfoResultDto> {
        return this.http
            .post<UserInfoResultDto>(URLConstants.ccAuthApiBaseUrl + 'login', classToPlain(loginParameterDto))
            .pipe(
                map((response: any) => {
                    const user = plainToClass(UserInfoResultDto, response);
                    if (Utils.notNullAndDefined(user)) {
                        this.setUser(user);
                        this.setToken(user.token);
                        this.setDecodedToken();
                    }
                    return user;
                })
            );
    }

    loginUsingFormsAuthentication(): Observable<boolean> {
        return this.http.post(URLConstants.ccAuthApiBaseUrl + 'formsauth', null).pipe(
            map((response: any) => {
                return this.verifyAndSetAuthenticatedUser(response);
            })
        );
    }

    clearAuthLocalStorageData(): void {
        localStorage.removeItem(StorageKeyConstants.apiTokenName);
        localStorage.removeItem(StorageKeyConstants.apiLoginTokenName);
        localStorage.removeItem(StorageKeyConstants.userInfo);
        localStorage.removeItem(StorageKeyConstants.loggedInUserInfo);
        localStorage.removeItem(StorageKeyConstants.apiIsShadowLogin);
    }

    getBrandingInformation(communityId: number, clearCache = false): Observable<BrandingConfigurationResultDto> {
        const url = `${URLConstants.ccBrandingApiBaseUrl}config?CommunityId=${communityId}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.http
            .get<BrandingConfigurationResultDto>(url, options)
            .pipe(map((resposeData) => plainToClass(BrandingConfigurationResultDto, resposeData)));
    }

    getBrandingImageData(imageUrl: string): Observable<Blob> {
        return this.http
            .get(imageUrl, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    setDecodedToken(): void {
        const token = localStorage.getItem(StorageKeyConstants.apiTokenName);
        if (!!token) {
            this.decodedToken = this.jwtHelper.decodeToken(token);
        }
    }

    private verifyAndSetAuthenticatedUser(user): boolean {
        if (!!user) {
            const token = user.token;
            if (!!token) {
                this.setUser(user);
                this.setToken(token);
                this.setDecodedToken();
                return true;
            }
        }

        return false;
    }

    private setUser(user: UserInfoResultDto) {
        localStorage.setItem(StorageKeyConstants.userInfo, JSON.stringify(user));
        localStorage.setItem(StorageKeyConstants.apiIsShadowLogin, JSON.stringify(user.isShadowLogin));
    }

    private setToken(token: string) {
        localStorage.setItem(StorageKeyConstants.apiTokenName, token);
    }
}
